import React, { useEffect, useState, useCallback, useMemo } from "react";
import { createPortal } from 'react-dom';
import { DndContext, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { useSensors, useSensor, PointerSensor, TouchSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import UniversalItemCardDraggable from "./UniversalItemCardDraggable";
import UniversalStorageGhost from "./UniversalStorageGhost";
import UniversalLibraryEmptyState from "./UniversalLibraryEmptyState";
import { useUniversalLibraryData } from "@/hooks/universal-storage/useUniversalLibraryData";
import { useUniversalStorageDnD } from "@/hooks/universal-storage/useUniversalStorageDnD";
import { useUniversalLibraryItemClick } from "@/hooks/universal-storage/useUniversalLibraryItemClick";
import ImageDetail from "@/components/image-lightbox/ImageDetail/ImageDetail";
import ResearchLightboxUniversal from "@/components/research-generator/ResearchLightboxUniversal";
import { ResearchItem } from "@/components/text-content-lightbox/types/ResearchLightboxTypes";
import { useResearchItemDetails } from "@/hooks/research-generator/use-research-item-details";
import SimplePageEditorModal from "@/components/private-text-generator/SimplePageEditorModal";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/errors/use-toast";
import { LibraryItem } from "@/contexts/UniversalLibraryContext/types";
import { ChevronLeft, ChevronRight, CloudDownload, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import GradientButton from '@/components/ui/GradientButton';
import StorageButton from './StorageButton';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';
import AddToProjectButton from '@/components/private-image-generator/AddToProjectButton';
import { PublicImageAddToProjectButton } from '@/components/image-generator';
import ResearchAddToProjectButton from '@/components/private-research-generator/AddToProjectButton';
import { PublicResearchAddToProjectButton } from '@/components/research-generator';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';

/** DND-enabled responsive grid for universal library items */
const UniversalLibraryGridDnD = () => {
  console.log('[UniversalLibraryGridDnD] Component rendering');
  
  const { items: rawItems, isLoading, error } = useUniversalLibraryData();
  
  // Stabilize items array to prevent unnecessary re-renders
  const items = useMemo(() => {
    console.log('[UniversalLibraryGridDnD] Memoizing items:', {
      rawItemsCount: rawItems?.length || 0,
      timestamp: new Date().toISOString()
    });
    return rawItems || [];
  }, [rawItems?.length, rawItems?.map(item => item.id).join(',')]);
  
  const [sortedItems, setSortedItems] = useState<LibraryItem[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Page editor modal state
  const [editingPageId, setEditingPageId] = useState<string | null>(null);

  console.log('[UniversalLibraryGridDnD] Hook data:', {
    itemsCount: items?.length || 0,
    isLoading,
    hasError: !!error,
    items: items?.map(item => ({
      id: item.id,
      title: item.title,
      contentType: item.contentType
    })),
    timestamp: new Date().toISOString()
  });

  // Update local sorted items when stabilized items change
  useEffect(() => {
    console.log('[UniversalLibraryGridDnD] Stabilized items changed, updating sorted items:', {
      newItemsCount: items.length,
      previousSortedCount: sortedItems.length,
      hasActualItems: items.length > 0
    });
    
    setSortedItems(items);
  }, [items]); // Now depends on the stabilized items array

  // DND functionality
  const { 
    activeItem, 
    handleDragStart, 
    handleDragEnd,
    isUpdating 
  } = useUniversalStorageDnD({
    items: sortedItems,
    onItemsReorder: setSortedItems,
  });

  // Lightbox functionality with custom handling for pages
  const {
    handleItemClick: originalHandleItemClick,
    lightboxItem,
    lightboxType,
    handleCloseLightbox,
    handleNavigateToItem,
  } = useUniversalLibraryItemClick();

  // Custom item click handler that intercepts private_page clicks
  const handleItemClick = useCallback((item: LibraryItem) => {
    if (item.contentType === 'private_page') {
      // For private pages, open the page editor modal
      const pageId = item.originalItem?.id || item.id;
      console.log('[UniversalLibraryGridDnD] Opening page editor for:', { pageId, item });
      setEditingPageId(pageId);
    } else {
      // For all other content types, use the original lightbox handler
      originalHandleItemClick(item);
    }
  }, [originalHandleItemClick]);

  // Page editor modal handlers
  const handleClosePage = useCallback(() => {
    setEditingPageId(null);
  }, []);

  const handlePageDeleted = useCallback((pageId: string) => {
    setEditingPageId(null);
    
    // Invalidate queries to refresh the storage list
    queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
    queryClient.invalidateQueries({ queryKey: ['universal-storage-counts'] });
    
    toast({
      title: "Page Deleted",
      description: "The page has been deleted successfully.",
      variant: "default",
    });
  }, [queryClient, toast]);

  // Research details for lightbox - only fetch for public research, use cached data for private research
  const publicResearchId = lightboxType === "research" && lightboxItem ? lightboxItem.originalItem?.id : null;
  const {
    data: publicResearchDetails,
    isLoading: isLoadingDetails,
  } = useResearchItemDetails(publicResearchId);

  // For private research, use the data that's already available in originalItem
  const researchDetails = lightboxType === "private_research" && lightboxItem?.originalItem 
    ? lightboxItem.originalItem 
    : publicResearchDetails;

  // Research lightbox navigation - route based on content type
  const handleRelatedQuestionClick = (researchId: string) => {
    console.log('[UniversalLibraryGridDnD] Navigating to research generator for research:', researchId);
    console.log('[UniversalLibraryGridDnD] Current lightbox type:', lightboxType);
    
    handleCloseLightbox();
    
    // Route to the appropriate research generator based on content type
    if (lightboxType === 'private_research') {
      console.log('[UniversalLibraryGridDnD] Routing to private research generator');
      navigate(`/private-research-generator?research=${researchId}`);
    } else {
      console.log('[UniversalLibraryGridDnD] Routing to public research generator');
      navigate(`/research-generator?research=${researchId}`);
    }
  };

  // Transform LibraryItem to ResearchItem for lightbox - handle both public and private research
  const transformResearchItemForLightbox = (item: LibraryItem): ResearchItem | null => {
    if (!item.originalItem || (item.contentType !== 'research' && item.contentType !== 'private_research')) {
      console.warn('[UniversalLibrary] Invalid research item for lightbox', item);
      return null;
    }
    
    const research = item.originalItem;
    return {
      id: research.id,
      title: research.title || "Untitled Research",
      content: research.content || "",
      researchType: research.research_type || "research",
      createdAt: research.created_at || new Date().toISOString(),
    };
  };

  // Custom sidebar for Universal Library (same as original)
  const renderUniversalLibrarySidebar = (item: LibraryItem, context: any) => {
    return (
      <div className="w-full lg:w-[250px] flex-shrink-0 lg:border-l border-accent/10 flex flex-col md:h-full md:max-h-[95vh]">
        <div className="flex flex-col md:h-full">
          {/* Navigation buttons at the top */}
          <div className="border-b border-accent/10 bg-background">
            <div className="p-3 md:p-4">
              <div className="flex justify-between items-center gap-3 w-full">
                <motion.button
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                    context.hasPrevious
                      ? "bg-gradient-to-r from-slate-100/80 to-gray-100/80 dark:from-slate-800/80 dark:to-gray-800/80 border-slate-200/50 dark:border-slate-700/50 text-slate-700 dark:text-slate-200 hover:from-slate-200/90 hover:to-gray-200/90 dark:hover:from-slate-700/90 dark:hover:to-gray-700/90 shadow-sm hover:shadow-md"
                      : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                  )}
                  onClick={context.hasPrevious ? context.onPrevious : undefined}
                  disabled={!context.hasPrevious}
                  whileHover={context.hasPrevious ? { scale: 1.02, y: -1 } : {}}
                  whileTap={context.hasPrevious ? { scale: 0.98 } : {}}
                >
                  <ChevronLeft size={16} className="flex-shrink-0" />
                  <span>Last</span>
                </motion.button>
                
                <motion.button
                  className={cn(
                    "flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex-1 backdrop-blur-sm border",
                    context.hasNext
                      ? "bg-gradient-to-r from-slate-800 to-slate-900 dark:from-slate-700 dark:to-slate-800 border-slate-700/50 dark:border-slate-600/50 text-white hover:from-slate-700 hover:to-slate-800 dark:hover:from-slate-600 dark:hover:to-slate-700 shadow-sm hover:shadow-md"
                      : context.isLoadingMore
                        ? "bg-gradient-to-r from-slate-700 to-slate-800 dark:from-slate-600 dark:to-slate-700 border-slate-600/50 dark:border-slate-500/50 text-white shadow-sm"
                        : "bg-slate-50/60 dark:bg-slate-900/60 border-slate-200/30 dark:border-slate-800/30 text-slate-400 dark:text-slate-600 cursor-not-allowed"
                  )}
                  onClick={() => {
                    if (context.hasNext && context.onNext && !context.isLoadingMore) {
                      context.onNext();
                    } 
                    else if (context.hasNextPage && !context.isLoadingMore && context.onLoadMore) {
                      context.onLoadMore();
                    }
                  }}
                  disabled={(!context.hasNext && !context.hasNextPage) || context.isLoadingMore}
                  whileHover={context.hasNext && !context.isLoadingMore ? { scale: 1.02, y: -1 } : {}}
                  whileTap={context.hasNext && !context.isLoadingMore ? { scale: 0.98 } : {}}
                >
                  {context.isLoadingMore ? (
                    <span className="text-sm font-medium bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent animate-pulse">
                      Loading...
                    </span>
                  ) : (
                    <>
                      <span>Next</span>
                      <ChevronRight size={16} className="flex-shrink-0" />
                    </>
                  )}
                </motion.button>
              </div>
            </div>
          </div>

          {/* Storage Button - positioned after navigation */}
          <div className="border-b border-accent/10 bg-background">
            <div className="p-3 md:p-4">
              <StorageButton 
                itemId={item.contentType === 'workbook' ? item.id : (item.originalItem?.id || item.id)}
                contentType={item.contentType as 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image' | 'private_research'}
                variant="compact"
                gradientFrom="from-blue-50/80 to-teal-50/80 dark:from-blue-950/30 dark:to-teal-950/30"
                gradientTo="hover:from-blue-100/80 hover:to-teal-100/80 dark:hover:from-blue-900/40 dark:hover:to-teal-900/40"
                accentColor="from-blue-400 to-teal-500"
                iconColor="text-blue-600 dark:text-blue-400"
                borderColor="border-blue-200/60 dark:border-blue-700/40"
                hoverBorderColor="hover:border-blue-300/70 dark:hover:border-blue-600/50"
                addText="Save to Storage"
                manageText="Manage Storage"
                addDescription="Add to your storage"
                manageDescription="Item saved"
                dropdownLabel="Add to Folder"
              />
            </div>
          </div>

          {/* Add to Project Button - positioned after Storage Button (for images and research) */}
          {(item.contentType === 'private_image' || item.contentType === 'image' || item.contentType === 'private_research' || item.contentType === 'research') && (
            <div className="border-b border-accent/10 bg-background">
              <div className="px-3 md:px-4 pt-2 md:pt-2 pb-3 md:pb-4">
                {item.contentType === 'private_image' ? (
                  <AddToProjectButton 
                    itemId={item.originalItem?.id || item.id}
                    dropdownLabel="Add to Project"
                    variant="compact"
                    contentType="private_image"
                    gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
                    gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
                    accentColor="from-purple-400 to-violet-500"
                    iconColor="text-purple-600 dark:text-purple-400"
                    borderColor="border-purple-200/60 dark:border-purple-700/40"
                    hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
                    addText="Add to Project"
                    manageText="Manage Project"
                    addDescription="Assign to a project"
                    manageDescription="Project assigned"
                  />
                ) : item.contentType === 'private_research' ? (
                  <ResearchAddToProjectButton 
                    itemId={item.originalItem?.id || item.id}
                    dropdownLabel="Add to Project"
                    variant="compact"
                    gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
                    gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
                    accentColor="from-purple-400 to-violet-500"
                    iconColor="text-purple-600 dark:text-purple-400"
                    borderColor="border-purple-200/60 dark:border-purple-700/40"
                    hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
                    addText="Add to Project"
                    manageText="Manage Project"
                    addDescription="Assign to a project"
                    manageDescription="Project assigned"
                  />
                ) : item.contentType === 'research' ? (
                  <PublicResearchAddToProjectButton 
                    itemId={item.originalItem?.id || item.id}
                    dropdownLabel="Add to Project"
                    variant="compact"
                    gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
                    gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
                    accentColor="from-purple-400 to-violet-500"
                    iconColor="text-purple-600 dark:text-purple-400"
                    borderColor="border-purple-200/60 dark:border-purple-700/40"
                    hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
                    addText="Add to Project"
                    manageText="Add to Project"
                    addDescription="Copy to a project"
                    manageDescription="Copy to a project"
                  />
                ) : (
                  <PublicImageAddToProjectButton 
                    itemId={item.originalItem?.id || item.id}
                    dropdownLabel="Add to Project"
                    variant="compact"
                    gradientFrom="from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30"
                    gradientTo="hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40"
                    accentColor="from-purple-400 to-violet-500"
                    iconColor="text-purple-600 dark:text-purple-400"
                    borderColor="border-purple-200/60 dark:border-purple-700/40"
                    hoverBorderColor="hover:border-purple-300/70 dark:hover:border-purple-600/50"
                    addText="Add to Project"
                    manageText="Add to Project"
                    addDescription="Copy to a project"
                    manageDescription="Copy to a project"
                  />
                )}
              </div>
            </div>
          )}

          {/* Simplified content area */}
          <div className="p-3 md:p-4 md:overflow-y-auto flex-1">
            {context.isOwner && (
              <div className="mt-6">
                <GradientButton
                  variant="red"
                  icon={<Trash2 className="h-4 w-4" />}
                  onClick={context.onDelete}
                  isLoading={context.isDeleteLoading}
                  disabled={context.isDeleteLoading}
                  fullWidth
                >
                  {context.isDeleteLoading ? 'Deleting...' : 'Delete'}
                </GradientButton>
              </div>
            )}
          </div>
          
          {/* Fixed bottom section with download button */}
          <div className="border-t border-accent/10 bg-background">
            <div className="p-3 md:p-4">
              <GradientButton
                variant="blue"
                icon={<CloudDownload className="h-4 w-4" />}
                onClick={context.onDownload}
                fullWidth
              >
                Download
              </GradientButton>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Custom delete handlers for different content types
  const handleCustomDelete = async (item: LibraryItem): Promise<void> => {
    const { contentType } = item;
    
    if (contentType === 'private_image') {
      // Use the same delete logic as Private Image Generator
      const imageId = item.originalItem?.id || item.id;
      
      // Get authenticated session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }
      
      // Get image data for storage path
      const { data: imageData, error: fetchError } = await supabase
        .from('workbook_images')
        .select('*')
        .eq('id', imageId)
        .single();

      if (fetchError) {
        throw new Error(`Failed to fetch image data: ${fetchError.message}`);
      }

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('workbook_images')
        .remove([imageData.storage_path]);

      if (storageError) {
        console.error('Storage deletion error (continuing anyway):', storageError);
      }

      // Delete from database - universal storage will cascade automatically
      const { error: dbError } = await supabase
        .from('workbook_images')
        .delete()
        .eq('id', imageId);

      if (dbError) {
        throw new Error(`Failed to delete image record: ${dbError.message}`);
      }
      
      // Note: Universal storage cleanup is now automatic via CASCADE foreign key
      
      // CRITICAL: Invalidate Universal Library caches immediately
      
      // Invalidate Universal Library data cache
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-storage-counts'] });
      
      // Invalidate Private Image Generator caches
      queryClient.invalidateQueries({ queryKey: ['private-image-generator'] });
      queryClient.invalidateQueries({ queryKey: ['private-image-generator-count'] });
      
      // Invalidate workbook image queries
      queryClient.invalidateQueries({ queryKey: ['workbook-images'] });
      
    } else if (contentType === 'image') {
      // Use existing public image delete logic
      const imageId = item.originalItem?.id || item.id;
      const { deleteImageFromStorage } = await import('@/hooks/runware/image-library/api/save-delete');
      const result = await deleteImageFromStorage(imageId);
      if (!result.success) {
        throw new Error(result.error || 'Failed to delete image');
      }
      
      // CRITICAL: Invalidate Universal Library caches for public images too
      queryClient.invalidateQueries({ queryKey: ['universal-storage-items'] });
      queryClient.invalidateQueries({ queryKey: ['universal-storage-counts'] });
      queryClient.invalidateQueries({ queryKey: ['imageLibrary'] });
      
    } else {
      throw new Error(`Delete not supported for content type: ${contentType}`);
    }
  };

  // Mobile-optimized sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle errors (same as original)
  useEffect(() => {
    if (error) {
      toast({
        variant: "destructive",
        title: "Failed to load library items",
        description:
          error?.message ||
          "An unknown error occurred in loading your library items. Please refresh the page or contact support.",
      });
    }
  }, [error, toast]);

  if (error) {
    return (
      <div className="flex flex-col items-center mt-10 text-rose-500">
        <div>Could not load items. Please try again later (or refresh).</div>
      </div>
    );
  }

  // Show loading state only while data is being fetched
  if (isLoading) {
    return (
      <div className={`grid ${GRID_BREAKPOINTS.documents} gap-5`}>
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 animate-pulse">
            {/* Top elements skeleton */}
            <div className="absolute top-2 left-2 z-10">
              <Skeleton className="w-6 h-6 rounded-full" />
            </div>
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-10">
              <Skeleton className="w-8 h-8 rounded-sm" />
            </div>
            <div className="absolute top-2 right-2 z-10">
              <Skeleton className="w-6 h-6 rounded-full" />
            </div>
            
            {/* Main content area skeleton */}
            <div className="absolute inset-0 flex items-center justify-center">
              <Skeleton className="w-16 h-16 rounded-lg opacity-30" />
            </div>
            
            {/* Bottom title area skeleton */}
            <div className="absolute bottom-0 left-0 right-0 p-3">
              <Skeleton className="w-3/4 h-4 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Show empty state when data is loaded and there are no items
  if (!isLoading && items.length === 0) {
    return <UniversalLibraryEmptyState />;
  }

  // Get item IDs for SortableContext
  const itemIds = sortedItems.map(item => item.id);

  // Filter images for navigation (include both regular images and private images)
  const imageItems = sortedItems.filter(item => item.contentType === "image" || item.contentType === "private_image");

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={rectSortingStrategy}
        >
          {/* The responsive grid */}
          <div className={`grid ${GRID_BREAKPOINTS.documents} gap-5`}>
            {sortedItems.map((item) => (
              <UniversalItemCardDraggable 
                key={item.id} 
                item={item} 
                onClick={handleItemClick} 
              />
            ))}
          </div>
        </SortableContext>

        {/* Drag overlay portal */}
        {typeof window !== 'undefined' && createPortal(
          <DragOverlay
            dropAnimation={null}
            modifiers={[]}
            zIndex={1000}
            style={{
              pointerEvents: 'none',
              willChange: 'transform',
              userSelect: 'none',
              touchAction: 'none',
            }}
          >
            {activeItem ? <UniversalStorageGhost item={activeItem} /> : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      {/* Image lightbox (for both regular images and workbook images) */}
      {(lightboxType === "image" || lightboxType === "private_image") && lightboxItem && (
        <ImageDetail<LibraryItem>
          image={lightboxItem}
          isOpen={!!lightboxItem}
          onClose={handleCloseLightbox}
          allImages={imageItems}
          onNavigate={handleNavigateToItem}
          
          getImageUrl={(item) => item.imageUrl || ""}
          getImageId={(item) => item.id}
          getImageDescription={(item) => item.title}
          getUserId={(item) => item.originalItem?.user_id}
          getFavoriteCount={(item) => item.originalItem?.favorite_count || 0}
          getIsFavorited={(item) => item.originalItem?.is_favorite || false} // NEW: Pass individual favorite status
          
          // Add custom delete handler for proper content type routing
          onDelete={handleCustomDelete}
          
          renderSidebar={renderUniversalLibrarySidebar}
          
          config={{
            navigation: { 
              wheel: true, 
              keyboard: true, 
              clickZones: true 
            },
            mobile: { 
              fullscreen: true, 
              sidebarPosition: 'bottom' 
            },
            preloading: {
              enabled: true,
              strategy: 'smart',
              preloadDistance: 2
            }
          }}
          
          // FIXED: Only show favorites for public images, not private images
          showFavoriteButton={lightboxItem.contentType === 'image'}
          showDescription={true}
          dropdownLabel="Add to Folder"
        />
      )}

      {/* Research lightbox - handle both public and private research */}
      {(lightboxType === "research" || lightboxType === "private_research") && lightboxItem && (
        <ResearchLightboxUniversal
          research={transformResearchItemForLightbox(lightboxItem)}
          isOpen={!!lightboxItem}
          onClose={handleCloseLightbox}
          researchDetails={researchDetails}
          isLoading={isLoadingDetails}
          onRelatedQuestionClick={handleRelatedQuestionClick}
          hasPrevious={false}
          hasNext={false}
          onPrevious={undefined}
          onNext={undefined}
          isOwner={lightboxType === "private_research"} // Private research is always owned
          onDelete={undefined}
          showFavoriteButton={lightboxType === "research"} // Only show favorites for public research
          contentType={lightboxType as 'research' | 'private_research'} // Pass the actual content type
        />
      )}

      {/* Page Editor Modal - for private_page content type */}
      <SimplePageEditorModal
        pageId={editingPageId}
        isOpen={!!editingPageId}
        onClose={handleClosePage}
        onPageDeleted={handlePageDeleted}
      />
    </>
  );
};

export default UniversalLibraryGridDnD; 